import { useState, useCallback } from 'react'
import Cropper, { type Area } from 'react-easy-crop'
import { X, Crop } from '@phosphor-icons/react'
import { Button } from './ui/button'

interface ImageCropperModalProps {
  file: File
  onConfirm: (croppedFile: File) => void
  onCancel: () => void
  /** Aspect ratio. 1 = square (default), 16/9, 4/3, etc. */
  aspect?: number
  /** Render the cropping mask as a circle (avatars). */
  cropShape?: 'rect' | 'round'
  /** Output side in pixels (square). Default 512. */
  outputSize?: number
  /** Output mime type. Default image/jpeg. PNG keeps transparency. */
  outputMime?: 'image/jpeg' | 'image/png' | 'image/webp'
  title?: string
}

async function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = (err) => reject(err)
    img.src = src
  })
}

async function getCroppedFile(
  imageSrc: string,
  pixelCrop: Area,
  outputSize: number,
  mime: string,
  originalName: string
): Promise<File> {
  const image = await loadImage(imageSrc)
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('canvas 2d unavailable')

  canvas.width = outputSize
  canvas.height = outputSize

  // Fill white when output is JPEG to avoid black corners from transparent pngs
  if (mime === 'image/jpeg') {
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, outputSize, outputSize)
  }

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    outputSize,
    outputSize
  )

  const blob: Blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('canvas.toBlob returned null'))),
      mime,
      0.92
    )
  })

  const ext = mime === 'image/png' ? 'png' : mime === 'image/webp' ? 'webp' : 'jpg'
  const baseName = (originalName.replace(/\.[^.]+$/, '') || 'image') + '_cropped'
  return new File([blob], `${baseName}.${ext}`, { type: mime })
}

export function ImageCropperModal({
  file,
  onConfirm,
  onCancel,
  aspect = 1,
  cropShape = 'rect',
  outputSize = 512,
  outputMime = 'image/jpeg',
  title = 'Ajuste a imagem'
}: ImageCropperModalProps) {
  const [imageSrc] = useState<string>(() => URL.createObjectURL(file))
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [pixelCrop, setPixelCrop] = useState<Area | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const onCropComplete = useCallback((_: Area, areaPixels: Area) => {
    setPixelCrop(areaPixels)
  }, [])

  async function handleConfirm() {
    if (!pixelCrop) return
    setBusy(true)
    setError(null)
    try {
      const cropped = await getCroppedFile(imageSrc, pixelCrop, outputSize, outputMime, file.name)
      onConfirm(cropped)
      URL.revokeObjectURL(imageSrc)
    } catch (err: any) {
      setError(err?.message || 'Falha ao processar imagem')
    } finally {
      setBusy(false)
    }
  }

  function handleCancel() {
    URL.revokeObjectURL(imageSrc)
    onCancel()
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={handleCancel} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[calc(100vh-2rem)] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b flex-shrink-0">
          <div className="flex items-center gap-2">
            <Crop size={20} className="text-emerald-600" />
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          </div>
          <button onClick={handleCancel} className="p-1.5 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100">
            <X size={20} />
          </button>
        </div>

        <div className="relative bg-gray-900 flex-1 min-h-[300px]" style={{ height: 360 }}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            cropShape={cropShape}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            objectFit="contain"
            showGrid
          />
        </div>

        <div className="px-5 py-4 border-t flex-shrink-0 space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Zoom</label>
            <input
              type="range"
              min={1}
              max={4}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="w-full accent-emerald-600"
            />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleCancel} disabled={busy}>Cancelar</Button>
            <Button onClick={handleConfirm} disabled={busy || !pixelCrop}>
              {busy ? 'Processando...' : 'Aplicar e enviar'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
